import { Button, Card, Checkbox, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import useLazyLoading from "../../../common/hooks/loader";
import Loader from "../../../components/loader";
import { useNotification } from "../../../components/notification/useNotification";
import { ITransactionFilterDto } from "../../../interfaces/filters/ITransactionFilterDto";
import { useFormik } from "formik";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { transactionTypeOptionList } from "../../../common/enum/transaction-type-enum";
import { tryGetAllWalletList } from "../../../services/wallet/wallet-service";
import extractErrorMessage from "../../../common/utils/errorMessages";
import { tryGetCryptoCurrencyListByUser } from "../../../services/crypto-currency/cripto-currency.service";
import IWalletDto from "../../../interfaces/wallet/IWalletDto";
import { ICryptoCurrency } from "../../../interfaces/crypto-currency/ICryptoCurrency";
import { useGlobalContext } from "../../../context/global-context";

export default function TransactionFilters() {
    const { displayNotification } = useNotification();
    const [loading, setLoading] = useState(false);
    const showLoading = useLazyLoading(loading);
    const [walletList, setWalletList] = useState<IWalletDto[]>();
    const [cryptoCurrencyList, setCryptoCurrencyList] = useState<ICryptoCurrency[]>();
    const { dispatch, state } = useGlobalContext();
    const { transactionFilters } = state;

    const formik = useFormik({
        initialValues: transactionFilters,
        validationSchema: null,
        enableReinitialize: true,
        onSubmit: async (values) => {
            dispatch({ type: 'SET_TRANSACTION_FILTERS', payload: values });
        }
    });

    async function getAllWalletList() {
        try {
            var response = await tryGetAllWalletList()

            if (response?.status === 200) {
                var walletList = response.data;
                setWalletList(walletList);
            }
        }
        catch (error) {
            displayNotification({ type: 'error', message: extractErrorMessage(error) });
        }
    }

    async function getCryptoCurrencyListByUser() {
        try {
            var response = await tryGetCryptoCurrencyListByUser();

            if (response?.status === 200) {
                var cryptoCurrencyList = response.data;
                setCryptoCurrencyList(cryptoCurrencyList);
            }
        }
        catch (error) {
            displayNotification({ type: 'error', message: extractErrorMessage(error) });
        }
    }

    useEffect(() => {
        setLoading(true);

        getAllWalletList();
        getCryptoCurrencyListByUser();

        setLoading(false);
    }, []);

    const handleCleanFilters = () => {
        dispatch({ type: 'RESET_TRANSACTION_FILTERS' });
        formik.resetForm();
    }

    return (
        <>
            {showLoading ? <Loader /> : null}
            <Card sx={{ mt: 2, border: '1px solid rgba(255,215,0,0.15)' }}>
                <form noValidate onSubmit={formik.handleSubmit}>
                    <Grid container spacing={3}>
                        <Grid sx={{ flex: 1 }}>
                            <Stack spacing={1} margin={2}>
                                <InputLabel sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                    Data Inicio
                                </InputLabel>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        value={
                                            formik.values.initialDate
                                                ? dayjs(formik.values.initialDate)
                                                : null
                                        }
                                        onChange={(value) => formik.setFieldValue('initialDate', value ? value.format("YYYY-MM-DD") : null)}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </Stack>
                        </Grid>
                        <Grid sx={{ flex: 1 }}>
                            <Stack spacing={1} margin={2}>
                                <InputLabel sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                    Data Fim
                                </InputLabel>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        value={
                                            formik.values.endDate
                                                ? dayjs(formik.values.endDate)
                                                : null}
                                        onChange={(value) => formik.setFieldValue('endDate', value ? value?.format("YYYY-MM-DD") : null)}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </Stack>
                        </Grid>
                        <Grid sx={{ flex: 1 }}>
                            <Stack spacing={1} margin={2}>
                                <InputLabel sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                    Tipo Operação
                                </InputLabel>
                                <Select
                                    labelId="transaction-type-label"
                                    id="transaction-type-select"
                                    size="small"
                                    value={formik.values.transactionType === null ? '' : formik.values.transactionType}
                                    onChange={(event) => formik.setFieldValue('transactionType', event.target.value)}
                                    error={Boolean(formik.touched.transactionType && formik.errors.transactionType)}
                                    MenuProps={{ disableScrollLock: true }}
                                >
                                    {transactionTypeOptionList && transactionTypeOptionList.map((option) => (
                                        <MenuItem
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Stack>
                        </Grid>
                        <Grid sx={{ flex: 1 }}>
                            <Stack spacing={1} margin={2}>
                                <InputLabel sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                    Carteira
                                </InputLabel>
                                <Select
                                    multiple
                                    value={formik.values.walletIdList}
                                    size="small"
                                    onChange={(event) => { formik.setFieldValue("walletIdList", event.target.value) }}
                                    MenuProps={{ disableScrollLock: true }}
                                    renderValue={(selected) =>
                                        walletList?.filter((op) => selected.includes(op.id)).map((op) => op.walletName).join(", ")
                                    }
                                >
                                    {walletList && walletList.map((wallet) => (
                                        <MenuItem
                                            key={wallet.id}
                                            value={wallet.id}
                                        >
                                            <Checkbox checked={formik.values.walletIdList?.includes(wallet.id)} />
                                            {wallet.walletName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Stack>
                        </Grid>
                        <Grid sx={{ flex: 1 }}>
                            <Stack spacing={1} margin={2}>
                                <InputLabel sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                    Cripto Moeda
                                </InputLabel>
                                <Select
                                    multiple
                                    value={formik.values.cryptoCurrencyIdList}
                                    size="small"
                                    onChange={(event) => {
                                        formik.setFieldValue("cryptoCurrencyIdList", event.target.value);
                                    }}
                                    MenuProps={{ disableScrollLock: true }}
                                    renderValue={(selected) =>
                                        cryptoCurrencyList?.filter((op) => selected.includes(op.id)).map((op) => op.name).join(", ")
                                    }
                                >
                                    {cryptoCurrencyList && cryptoCurrencyList.map((cryptoCurrency) => (
                                        <MenuItem
                                            key={cryptoCurrency.id}
                                            value={cryptoCurrency.id}
                                        >
                                            <Grid container justifyContent="center" alignItems="center">
                                                <Checkbox checked={formik.values.cryptoCurrencyIdList?.includes(cryptoCurrency.id)} />
                                                <Grid container justifyContent="center" alignItems="center" spacing={0.5}>
                                                    <img src={cryptoCurrency.imageUrl} width="20" height="20" />
                                                    {cryptoCurrency.name}
                                                </Grid>
                                            </Grid>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Stack>
                        </Grid>
                    </Grid>
                    <Grid container justifyContent="flex-end" marginBottom={1} marginRight={2}>
                        <Grid marginRight={2}>
                            <Button
                                disableElevation
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={() => handleCleanFilters()}
                            >
                                Limpar Filtros
                            </Button>
                        </Grid>
                        <Grid>
                            <Button
                                disableElevation
                                disabled={formik.isSubmitting}
                                size="small"
                                type="submit"
                                variant="contained"
                                color="primary"
                            >
                                Filtrar
                            </Button>
                        </Grid>


                    </Grid>
                </form>
            </Card>
        </>
    )
}